import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SituViewWebApiClient,
  FileVaultReference,
  FileVaultCategory,
  GetFileVaultReferencesRequest
} from 'situview-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from 'src/app/services/config.service';
import { Subject } from 'rxjs';
import { AuthService, BreadcrumbService, StateService, Utils } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { debounceTime } from 'rxjs/operators';
import { FormControl } from '@angular/forms';

export class FileVaultSelectorDialogData {
  companyId!: string;
  fileVaultCategory?: FileVaultCategory;
  validFileExtensions!: string[];
  fileHashFilter?: string;
}

export interface FileVaultSelectorResultData {
  fileVaultReferences: FileVaultReference[];
  category?: FileVaultCategory;
}

@Component({
  selector: 'file-vault-selector-dialog',
  templateUrl: './file-vault-selector-dialog.component.html',
  styleUrls: ['./file-vault-selector-dialog.component.scss']
})
export class FileVaultSelectorDialogComponent extends SituBaseComponent {
  /* Search */
  pageSize = 100;
  pageNumber = 1;
  totalCount = 0;

  /* Data */
  dataSource = new Array<FileVaultReferenceItem>();

  searchTerm: string = "";
  fileExtensionFilter = new FormControl();

  /* Category */
  selectedCategory: FileVaultCategory | undefined;
  categoryOptions: { value: FileVaultCategory; label: string }[] = [];
  showCategorySelector = false;

  /* Loading */
  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    @Inject(MAT_DIALOG_DATA) public data: FileVaultSelectorDialogData,
    private breadcrumbService: BreadcrumbService,
    private dialogRef: MatDialogRef<FileVaultSelectorDialogData>,
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef,
    private situViewClient: SituViewWebApiClient) {
      super(authService, stateService);

      this._subscriptions.push(this.searchTermChangedSubject.pipe( debounceTime(600) )
      .subscribe(() => {
        this.searchTermChanged();
      }))
      
    }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();

    if (this.data.fileVaultCategory) {
      this.selectedCategory = this.data.fileVaultCategory;
      this.loadFiles(true);
    } else {
      await this.loadAuthorizedCategories();
      this.showCategorySelector = true;
    }
  }

  private async loadAuthorizedCategories() {
    try {
      const categories = await this.situViewClient.getAuthorizedFileVaultCategories();
      this.categoryOptions = categories.map(c => ({ value: c, label: this.getCategoryLabel(c) }));
    } catch {
      this.categoryOptions = [];
    }
  }

  private getCategoryLabel(category: FileVaultCategory): string {
    switch (category) {
      case FileVaultCategory.ReferenceMaterial: return 'Reference Material';
      case FileVaultCategory.SourceMaterial: return 'Source Material';
      case FileVaultCategory.Assets: return 'Assets';
      case FileVaultCategory.MarketingMaterial: return 'Marketing Material';
      case FileVaultCategory.Logo: return 'Logo';
      default: return 'Unknown';
    }
  }

  onCategorySelected(category: FileVaultCategory) {
    this.selectedCategory = category;
    this.showCategorySelector = false;
    this.loadFiles(true);
  }

  ngOnDestroy(): void {
    if (this.searchTermChangedSubject) {
      this.searchTermChangedSubject.unsubscribe();
    }
  }

  private searchTermChangedSubject = new Subject();
  debounceSearchTermChanged() {
    this.searchTermChangedSubject.next();
  }

  searchTermChanged() {
    this.filterFiles();
  }

  filterFiles() {
    this.pageNumber = 1;
    this.loadFiles(true);
  }

  async loadFiles(clearExistingFiles: boolean) {
    if (this.loading) {
      return;
    }
    
    this.loading = true;

    
    try {

      var request = new GetFileVaultReferencesRequest();
      request.companyId = this.data.companyId;
      request.category = this.selectedCategory!;
      request.fileNameFilter = this.searchTerm,
      request.fileHashFilter = this.data.fileHashFilter,
      request.extensionsFilter = this.fileExtensionFilter.value
      request.pageNumber = this.pageNumber;
      request.pageSize = this.pageSize;

      var fileVaultReferencePage = await this.situViewClient.getFileVaultReferences(request);
      
      if (!fileVaultReferencePage.success) {
        this.showMessage("There was an issue loading file vault files. Please refresh the page to try again.");
        return;
      }
      
      if (clearExistingFiles) {
        this.dataSource = fileVaultReferencePage.items! as FileVaultReferenceItem[];
      } else {
        this.dataSource.push(...fileVaultReferencePage.items! as FileVaultReferenceItem[]);
      }

      this.totalCount = fileVaultReferencePage.totalItems!;

    } finally {
      this.loading = false;
    }
  }

  selectFileVaultReference(item: FileVaultReferenceItem) {
    item.isSelected = !item.isSelected;
  }

  chooseSelectFileVaultReferences() {
    var returnData = {
      fileVaultReferences: this.dataSource.filter(x => x.isSelected),
      category: this.selectedCategory
    } as FileVaultSelectorResultData;
    this.dialogRef.close(returnData);
  }

  getSelectedFileVaultReferenceCount() {
    return this.dataSource.filter(x => x.isSelected).length;
  }

  pluralize(unit: string, value: number): string {
    return Utils.pluralize(unit, value);
  }
  
  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class FileVaultReferenceItem extends FileVaultReference {
  isSelected = false;
}
